import { DependencyList, useCallback, useEffect, useRef } from 'react'

/**
 * Calls `fn` once `ms` has passed without `deps` changing. The timer is also
 * armed on mount, so `fn` runs once after the first `ms` even if `deps` never
 * change. Returns `[isReady, cancel]`; `cancel` is stable while `ms` is.
 *
 * Ported from react-use (Unlicense) to keep its timing exactly.
 */
export const useDebounce = (
  fn: () => void,
  ms = 0,
  deps: DependencyList = [],
): [() => boolean | null, () => void] => {
  const ready = useRef<boolean | null>(false)
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined)
  const callback = useRef(fn)

  const isReady = useCallback(() => ready.current, [])

  const set = useCallback(() => {
    ready.current = false
    if (timeout.current) clearTimeout(timeout.current)
    timeout.current = setTimeout(() => {
      ready.current = true
      callback.current()
    }, ms)
  }, [ms])

  const clear = useCallback(() => {
    ready.current = null
    if (timeout.current) clearTimeout(timeout.current)
  }, [])

  useEffect(() => {
    callback.current = fn
  }, [fn])

  useEffect(() => {
    set()
    return clear
  }, [ms])

  useEffect(set, deps)

  return [isReady, clear]
}
